/* ============================================================================
 * assets/bundle-upsell.js — Bundle System D7 upsell engine (client surface)
 * Rock 4 run R4.2. The behavioral spec is the offers-wireframe (§1 state machine,
 * §2 per-render decision flow). Every branch below cites its rule id (R-*).
 * ----------------------------------------------------------------------------
 * WHAT:  Drives the cart upsell card (snippets/bundle-upsell-card.liquid). On
 *        cart render + every cart change it runs the decision flow against live
 *        cart contents + the R4.1 synced config, renders exactly one card (or
 *        none), and on accept performs the PROVEN swap and emits telemetry.
 * HOW:   Custom element <bundle-upsell-card>. Reads the compiled offer config
 *        from its inline JSON. Subscribes to the theme's `cart-update` pubsub so
 *        it re-evaluates after any line change. No new third-party JS; leans on
 *        the theme globals (subscribe/publish/PUB_SUB_EVENTS/routes/fetchConfig)
 *        with typeof guards so it degrades instead of throwing.
 *
 * SWAP PROTOCOL (proven on the real store 2026-07-23; D7 F16):
 *   PRIMARY  = SINGLE-REQUEST /cart/update.js — one request zeroes the component
 *              line(s) AND adds the bundle variant AND self-writes the bundle_id/
 *              bundle_src cart attributes (D5). Proven atomic (a bad element
 *              rejects the whole request — nothing partial) and cart-conserved to
 *              the cent for a like-for-like swap.
 *   FALLBACK = sequential ADD-BUNDLE-FIRST → verify → zero component. Used only
 *              when config.swap_mode === 'sequential' (i.e. the dev-store probe
 *              disproved single-request atomicity). Fails toward a VISIBLE
 *              duplicate (loud + recoverable), never silently-missing merchandise.
 *   EITHER PATH: validate the returned cart against the conservation check; on
 *              mismatch show a reload error and make NO compensating blind mutation.
 *
 * KILL SWITCH (ADR-3 / F14 / SC-8 — FAIL CLOSED): render-time kill handled in
 *   Liquid (surfaces.cart + bundle_engine belt). This JS ALSO re-checks an UNCACHED
 *   runtime signal (config.action_kill_url) at ACTION time before any accept/swap —
 *   because a cached cart page (EX-006) can serve stale "enabled" markup for hours.
 *   The check implements the FROZEN contract upsell_engine.action_time_killcheck_
 *   contract(): GET the endpoint no-store with a hard 1500ms timeout; the JSON body
 *   is { surface, enabled(bool), revision, checked_at } and `enabled` is the ONLY
 *   field trusted. The action proceeds ONLY on a 200 with enabled === true. A
 *   non-200, a timeout, a network error, a malformed body, or a MISSING URL ALL
 *   read as KILLED — never a silent proceed (SC-8: the endpoint erroring must still
 *   abort). The R4.1 app-proxy seam is landed (the engine emits action_kill_url on
 *   every compile), so a rendered card with no URL is a misconfiguration that fails
 *   closed, not a degrade-to-proceed.
 *
 * NULL OFFER (D4 / SC-4 front half): when the compiled config marks the cart's
 *   routine already covered (cfg.null_offers), the surface renders NOTHING and
 *   fires the null-offer log — a GA4 `bundle_upsell_null_offer` event (aggregate
 *   rail -> upsell_events ETL, wi-2026-0629) AND a best-effort POST to the offer-
 *   history endpoint (per-customer rail). Suppression must be MEASURABLE; a silent
 *   non-render is indistinguishable from a bug.
 *
 * FREE-SHIP LINE (W truth guard / wi-2026-0649 P0): the line is composed from the
 *   POST-ACCEPT cart (the cart the shopper would have the instant they tap the CTA),
 *   never the pre-accept one. Post-accept clears the threshold => the COMPILED
 *   framing.free_shipping_line; still short => the "Add $X" form measured from the
 *   POST gap; already qualifying, a subscription cart, or an uncomputable post state
 *   => no line at all. Before this fix the card told the shopper to go add money
 *   elsewhere on essentially every below-threshold impression, while its own CTA
 *   already crossed the threshold (offer-copy doc F7).
 *
 * ACCEPT SUPPRESSION (R-ACCEPTED / wi-2026-0649 P0, doc S1): one sessionStorage key
 *   written after a VALIDATED accept silences every render + every telemetry rail
 *   for the rest of the session. Session-scoped, not cart-scoped, on purpose.
 *
 * OFFER HISTORY (D13 / SC-19): every rendered offer records an impression (surface
 *   + variant serving envelope) to the offer-history endpoint, AND every accept /
 *   dismiss records its OUTCOME through the same `record` op with the outcome named
 *   (wi-2026-0649 P1: the durable ledger used to know what we showed and nothing
 *   about what happened). When there are
 *   multiple applicable offers the runtime CONSUMES the rotation choice from that
 *   endpoint so the same card is not re-shown forever. All history calls are best-
 *   effort (a recording failure never breaks the cart) and key-gated: an identified
 *   customer or a durable anon session key (a functional cookie owned by a later
 *   theme unit) enables per-customer rotation; a keyless session degrades to the
 *   status quo (first applicable wins), with the GA4 rail still measuring.
 *
 * ===== SEAMS (R4.1 not landed 2026-07-23) ==================================
 *   - The whole offer config is authored/compiled by R4.1 (upsell_engine.py) and
 *     written to shop metafield bundle_engine.upsell_config by provisioning. Until
 *     then the Liquid emits no <bundle-upsell-card>, so this file is inert.
 *   - action_kill_url is the F14 uncached endpoint (app-proxy | Storefront-API |
 *     section-render fetch — mechanism picked + dev-store-verified in R4.1). This
 *     file consumes whatever URL the config hands it; absent ⇒ documented degrade.
 *   - Owned-suppression (R-OWN) needs identified-customer state (R4.1 per-customer
 *     artifact). Config may carry `owned_product_ids`; absent ⇒ NO suppression
 *     (graceful degrade, never a pretended signal — D7 cut-list #3).
 *   - Live free-ship threshold read is the truth-guard PRIMARY; here we use the
 *     config fallback + suppress on subscription carts (subs ship free). Live read
 *     is a seam.
 *
 * GA4 CONTRACT (exact param names — a mismatch silently empties the dimension
 *   forever; registered 2026-07-23 property 423458781): upsell_surface,
 *   upsell_offer_type, upsell_trigger_product, upsell_variant, bundle_id.
 *   upsell_variant carries the CAST SKIN this serve rendered (mirrors the PDP
 *   block's cfg.serving.casting -> skin), so the (variant x device) readout
 *   (wi-2026-0639) splits take-rate by SKIN. The swap/add/swap_to_save CTA
 *   classification is real signal too, but it is NOT one of the five registered
 *   params and the ETL does not read it — it rides along as upsell_cta_variant
 *   for analysis only. Before the offer-surface-skins serve-path fix the cart
 *   emitted the CTA type as upsell_variant, colliding with the skin dimension —
 *   if switched on, cart rows would have split by swap/add, not by skin.
 * COPY RULES (OM): no capsule/serving counts; savings shown as money only.
 * ========================================================================== */

(function () {
  "use strict";

  // Headless selftest seam (assets/bundle-upsell.selftest.js): under Node there is
  // no DOM, so export the pure helpers and stop before any element is defined.
  if (typeof window === "undefined" && typeof module !== "undefined" && module.exports) {
    module.exports = { stepUpLookup: stepUpLookup };
    return;
  }

  // ---- Theme-global shims (degrade instead of throw if load order slips) ----
  var CART_UPDATE_EVENT =
    (typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS.cartUpdate) || "cart-update";
  function themeSubscribe(evt, cb) {
    if (typeof subscribe === "function") return subscribe(evt, cb);
    return function () {};
  }
  function themePublish(evt, data) {
    if (typeof publish === "function") publish(evt, data);
  }
  function cartRoot() {
    // Theme exposes `routes`; fall back to conventional AJAX paths.
    var r = (typeof routes !== "undefined" && routes) || {};
    return {
      cart: r.cart_url || "/cart",
      add: r.cart_add_url || "/cart/add",
      change: r.cart_change_url || "/cart/change",
      update: r.cart_update_url || "/cart/update",
    };
  }
  function jsonFetchConfig() {
    // Mirror the theme's fetchConfig for JSON if available; else a sane default.
    if (typeof fetchConfig === "function") {
      try { return fetchConfig("json"); } catch (e) { /* fall through */ }
    }
    return {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
    };
  }

  // ---- Per-surface framing read ----
  /* THE PER-SURFACE FRAMING READ (2026-08-22 copy register). The compiled
   * offer_variant now carries framing_by_surface[<surface>] as well as the
   * flat `framing` (which is the PDP block). This file is the CART runtime,
   * so it asks for the cart's words and falls back to the flat block for a
   * config compiled before the per-surface fold existed. No copy here. */
  function surfaceFraming(ov, surface) {
    if (!ov) return {};
    var bySurface = ov.framing_by_surface;
    if (bySurface && bySurface[surface]) return bySurface[surface];
    return ov.framing || {};
  }

  // ---- Money formatting (Shopify helper if present, else $X.XX) ----
  function money(cents) {
    var n = (cents || 0) / 100;
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      try { return window.Shopify.formatMoney(cents, window.Shopify.money_format); }
      catch (e) { /* fall through */ }
    }
    return "$" + n.toFixed(2);
  }

  // ---- Size-label -> day count (per-day math for the honest-math receipt) ----
  function daysFromSize(sizeLabel) {
    var m = String(sizeLabel || "").match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 30;
  }

  // ---- Telemetry ------------------------------------------------------------
  // GA4 custom events ride the existing gtag/GTM dataLayer (ADR-2). Exact params.
  var _emitted = {}; // impression dedupe per (surface|bundle|skin) this pageview
  function emitGA4(payload) {
    if (!payload || !payload.event) return;
    if (payload.event === "bundle_upsell_impression") {
      // Keyed on upsell_variant, which now carries the cast SKIN (constant per
      // pageview) — one impression per (surface|bundle|skin), not per CTA label.
      var k = payload.upsell_surface + "|" + payload.bundle_id + "|" + payload.upsell_variant;
      if (_emitted[k]) return;
      _emitted[k] = true;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }
  // `skin` is the cast skin (the serve fact -> upsell_variant, matching PDP);
  // `ctaVariant` is the swap/add classification (real signal -> upsell_cta_variant,
  // an analysis-only param the ETL does not read). Keeping them separate is the
  // offer-surface-skins serve-path fix: the (variant x device) readout must split
  // by skin, so the CTA type can no longer masquerade as upsell_variant.
  function ga4Event(name, surface, offer, skin, ctaVariant, triggerProductId) {
    emitGA4({
      event: name,
      upsell_surface: surface,
      upsell_offer_type: (offer && offer.offer_type) || "bundle_completion",
      upsell_trigger_product: triggerProductId != null ? triggerProductId : "",
      upsell_variant: skin,               // the CAST SKIN — matches the PDP contract
      upsell_cta_variant: ctaVariant,     // swap | swap_to_save | add (analysis only)
      bundle_id: (offer && offer.bundle_id) || "",
    });
  }
  // Cart add/remove tracking: Elevar's automatic listener on the /cart/update.js
  // write IS the one event per line. A manual dl_add_to_cart/dl_remove_from_cart
  // push here double-counted every swap (2 adds + 3 removes for one swap, O9 QA
  // 2026-09-30; same class as red team #7). Kept as a no-op so the call sites and
  // the node selftest are unchanged; bundle attribution rides bundle_id/bundle_src.
  function elevarCart(kind, elevarProduct, qty) { return; }

  // ---- Dismissal memory: session-scope, per-OFFER key (OPEN-3 default) ------
  /* CART STEP-UP pure lookup (wi-2026-1122), exported for the node selftest.
   * productIds = the cart's line product ids (repeats fine). */
  function stepUpLookup(su, productIds) {
    var seen = {}, ids = [];
    (productIds || []).forEach(function (p) { var n = Number(p); if (!seen[n]) { seen[n] = true; ids.push(n); } });
    if (!ids.length) return [];
    var bundles = su.bundle_product_ids || [];
    for (var i = 0; i < ids.length; i++) { if (bundles.indexOf(ids[i]) !== -1) return []; }
    ids.sort(function (a, b) { return a - b; });
    return ((su.by_cart || {})[ids.join(",")] || []).slice();
  }

  function dismissKey(bundleId) { return "om_bundle_upsell_dismissed:" + bundleId; }
  function isDismissed(bundleId) {
    try { return sessionStorage.getItem(dismissKey(bundleId)) === "1"; }
    catch (e) { return false; }
  }
  function setDismissed(bundleId) {
    try { sessionStorage.setItem(dismissKey(bundleId), "1"); } catch (e) {}
  }

  /* ---- Accept suppression: session-scope, ONE key, no per-offer keying ------
   * R-ACCEPTED (wi-2026-0649 P0; offer-copy doc section 5.2 rule S1, operator
   * approved): once a shopper says yes to ANY offer, the pitching stops for the
   * REST OF THE SESSION. Scoped to the session and deliberately NOT to cart
   * contents (doc 5.3 refinement 1): if they accept and later remove the bundle
   * the cart state reverts, but the conversation still happened, and re-pitching
   * after a removal is the classic "the machine isn't listening" moment.
   * sessionStorage IS the session scope, so there is no expiry math to get wrong.
   * Every access is try/catch wrapped: private browsing throws on write, and some
   * user agents throw on read too. Storage unavailable degrades to the old
   * behavior (pitch as before), never to a throw. */
  var ACCEPTED_KEY = "om_bundle_upsell_accepted";
  function acceptedThisSession() {
    try { return !!sessionStorage.getItem(ACCEPTED_KEY); }
    catch (e) { return false; }
  }
  function setAcceptedThisSession(bundleId, ctaVariant, surface) {
    // JSON so the flag carries WHICH offer closed and WHEN (useful to a later
    // durable-ledger reconcile); the gate itself only tests key presence.
    try {
      sessionStorage.setItem(ACCEPTED_KEY, JSON.stringify({
        bundle_id: bundleId || "",
        cta_variant: ctaVariant || "",
        surface: surface || "cart",
        accepted_at: Date.now()
      }));
    } catch (e) {}
  }

  // ---- Cart helpers ---------------------------------------------------------
  // A failed read (e.g. Shopify's 429 when the cart is busy, or a non-cart body)
  // used to resolve as a cart with no items and the card hid itself, so the swap
  // offer vanished on exactly the busy moment after an add (O9 2026-09-30).
  // Now: a non-OK response or a body without items[] is retried ONCE after a
  // short pause, and only then rejects (the caller hides, as before).
  function getCartOnce() {
    return fetch(cartRoot().cart + ".js", { headers: { Accept: "application/json" }, cache: "no-store" })
      .then(function (r) {
        if (!r.ok) throw new Error("cart " + r.status);
        return r.json();
      })
      .then(function (c) {
        if (!c || !Array.isArray(c.items)) throw new Error("cart body");
        return c;
      });
  }
  function getCart() {
    return getCartOnce().catch(function () {
      return new Promise(function (res) { setTimeout(res, 900); }).then(getCartOnce);
    });
  }

  /* ===========================================================================
   * The custom element
   * ========================================================================= */
  class BundleUpsellCard extends HTMLElement {
    constructor() {
      super();
      this._busy = false;      // click-lock against double-fire (R-CARTMATH safety)
      this._shownOffer = null; // for decline-at-journey-end
      this._shownVariant = null;
      this._shownTrigger = null;
      this._recorded = {};     // per-pageview impression-record dedupe (offer history)
    }

    connectedCallback() {
      var cfgEl = this.querySelector("[data-buc-config]");
      try { this.config = JSON.parse(cfgEl.textContent); }
      catch (e) { this.config = null; }
      this.template = this.querySelector("[data-buc-template]");
      this.mount = this.querySelector("[data-buc-mount]");
      this.live = this.querySelector("[data-buc-live]");

      if (!this.config || !Array.isArray(this.config.offers)) return;

      // STAGING IN-SITU PREVIEW (wi-2026-0644 review fix): the Liquid preview
      // branch un-hides the server-rendered mount on unpublished themes; the
      // live-cart evaluation would immediately wipe it (empty cart => no
      // decision => hide()). A preview render is a static review instrument:
      // keep the server markup, skip evaluation + cart subscription entirely.
      if (this.dataset.insituPreview === "true") return;

      // Re-evaluate on every cart change (theme pubsub) + once now.
      this._unsub = themeSubscribe(CART_UPDATE_EVENT, this.evaluate.bind(this));
      this.evaluate();

      // Re-evaluate whenever the CART DRAWER opens (O9 2026-09-30, must-ship-ON).
      // The card only re-read the cart on page load and on a published cart
      // update, so a cart that changed without a publish (an add from another
      // tab or app, a direct /cart/add.js, a restored session) opened the drawer
      // with the stale page-load decision: an empty cart => hidden, and no swap
      // offer even though /cart showed one. Watching the drawer's `active` class
      // makes every open read the real cart. Page surfaces (/cart) are unaffected.
      var drawer = this.closest && this.closest("cart-drawer");
      if (drawer && typeof MutationObserver !== "undefined") {
        var self = this, wasOpen = drawer.classList.contains("active");
        this._drawerObs = new MutationObserver(function () {
          var isOpen = drawer.classList.contains("active");
          if (isOpen && !wasOpen) self.evaluate();
          wasOpen = isOpen;
        });
        this._drawerObs.observe(drawer, { attributes: true, attributeFilter: ["class"] });
      }

      // Decline: shown-but-not-accepted at journey end (navigate away).
      this._onHide = this.onPageHide.bind(this);
      window.addEventListener("pagehide", this._onHide, { once: false });
    }

    disconnectedCallback() {
      if (this._unsub) this._unsub();
      if (this._drawerObs) this._drawerObs.disconnect();
      window.removeEventListener("pagehide", this._onHide);
    }

    // ---- §2 decision flow. `decide` keeps its old single-pick contract (first
    // applicable wins); `applicableDecisions` returns the FULL ordered candidate
    // set so the runtime can rotate across it (D13/SC-19). The per-offer gate
    // logic is unchanged — only the "return first" became "collect all".
    decide(cart) {
      var d = this.applicableDecisions(cart);
      return d.length ? d[0] : null;
    }

    applicableDecisions(cart) {
      var cfg = this.config;
      var decisions = [];

      // R-KILL render-time already handled in Liquid (no element emitted). Here we
      // trust the emitted element; the ACTION-time uncached check runs on accept.

      // Bundle-already-in-cart (R-MOOT): the bundle pitch is moot. v1 sub pitch is
      // dark, so the slot is simply empty (offers-wireframe OPEN-5).
      var bundleVariantIds = this.allBundleVariantIds();
      var inCartVariantIds = {};
      cart.items.forEach(function (it) {
        inCartVariantIds[it.variant_id] = (inCartVariantIds[it.variant_id] || 0) + it.quantity;
      });
      for (var i = 0; i < bundleVariantIds.length; i++) {
        if (inCartVariantIds[bundleVariantIds[i]]) return []; // a bundle is already in cart
      }

      // CART STEP-UP (wi-2026-1122): the compiled config decides WHICH bundle this
      // cart may be offered; this file only looks the answer up. Key = the cart's
      // distinct product ids, ascending, comma-joined (upsell_engine._stepup_key).
      // A bundle product in the cart, or a key the compiler did not emit (3+
      // products, a product in no 2-product bundle, a pair no 3-product bundle
      // holds), means NO offer. Absent cfg.cart_stepup = the pre-1122 behaviour.
      var stepAllowed = this.stepUpAllowed(cart);
      if (stepAllowed && !stepAllowed.length) return [];

      // Walk offers; collect every applicable one in rank order (config order).
      // Rotation (chooseByRotation) picks among them; with no rotation key the
      // first applicable wins, exactly as before (SOFT max 1 card per surface).
      for (var o = 0; o < cfg.offers.length; o++) {
        var offer = cfg.offers[o];

        // CART STEP-UP: only the bundles the compiled table allows for this cart.
        if (stepAllowed && stepAllowed.indexOf(offer.bundle_id) === -1) continue;

        // R-GIFT: never pitch a welcome-kit / unlisted gift path.
        if (offer.gift === true) continue;
        if (this.cartHasGift(cart, offer)) continue;

        // R-DISMISS: dismissed this session ⇒ zero further renders of THIS offer.
        if (isDismissed(offer.bundle_id)) continue;

        // R-OWN: suppress-already-owned — identified sessions only. Config may carry
        // owned_product_ids (R4.1 seam); absent ⇒ NO suppression (graceful degrade).
        if (this.ownsAllTriggers(offer)) continue;

        // Find a trigger line: a cart item whose product is a trigger for this offer.
        var triggerItem = null;
        for (var k = 0; k < cart.items.length; k++) {
          if (offer.trigger_product_ids && offer.trigger_product_ids.indexOf(cart.items[k].product_id) !== -1) {
            triggerItem = cart.items[k];
            break;
          }
        }
        if (!triggerItem) continue;

        // Resolve the offer variant for this trigger (per-size map, else default).
        var ov = this.resolveVariant(offer, triggerItem);
        if (!ov || ov.available === false) continue; // R-OOS: never pitch unavailable

        // R-STS: cart holds ALL components ⇒ swap-to-save. Evaluated BEFORE line
        // qty (whole-set precedence, OPEN-4).
        if (this.cartHasAllComponents(cart, offer)) {
          decisions.push({ offer: offer, variant: "swap_to_save", ov: ov, trigger: triggerItem });
          continue;
        }
        // R-SWAP vs R-ADD: qty 1 swaps; qty 2+ adds (never zero a multi-qty line).
        if (triggerItem.quantity === 1) {
          decisions.push({ offer: offer, variant: "swap", ov: ov, trigger: triggerItem });
          continue;
        }
        decisions.push({ offer: offer, variant: "add", ov: ov, trigger: triggerItem });
      }
      // Step-up rank is the compiler's (authored default first), not config order;
      // and one card per bundle, whichever cart line triggered it.
      if (stepAllowed) {
        var seenB = {};
        decisions = decisions.filter(function (d) {
          if (seenB[d.offer.bundle_id]) return false;
          seenB[d.offer.bundle_id] = true; return true;
        });
        decisions.sort(function (a, b) {
          return stepAllowed.indexOf(a.offer.bundle_id) - stepAllowed.indexOf(b.offer.bundle_id);
        });
      }
      return decisions;
    }

    /* CART STEP-UP lookup (wi-2026-1122). Returns null when the config carries no
     * step-up table (older compile: behave exactly as before), else the ranked
     * bundle_ids this cart may be offered ([] = offer nothing). Pure lookup: the
     * rule itself lives in upsell_engine.build_cart_stepup. */
    stepUpAllowed(cart) {
      var su = this.config && this.config.cart_stepup;
      if (!su || !su.by_cart) return null;
      return stepUpLookup(su, (cart.items || []).map(function (it) { return it.product_id; }));
    }

    /* D4/SC-4: is the cart's routine already COVERED? The compiled config marks a
     * covered routine (cfg.null_offers, populated by the compile half wi-2026-0622).
     * Each entry names the product ids whose full presence means "no gap":
     *   covered_product_ids (preferred) | all_component_product_ids (fallback),
     *   gated by trigger_product_ids (the customer must be assembling the routine),
     *   with bundle_id | trigger_sku as the covered-routine identity for logging.
     * Absent cfg.null_offers ⇒ NO suppression (graceful degrade, status quo).
     * Returns the matching null-offer entry, or null. */
    coveredNullOffer(cart) {
      var nulls = (this.config && this.config.null_offers) || [];
      if (!nulls.length) return null;
      var inCartProductIds = {};
      cart.items.forEach(function (it) { inCartProductIds[it.product_id] = true; });
      for (var i = 0; i < nulls.length; i++) {
        var n = nulls[i];
        var covered = n.covered_product_ids || n.all_component_product_ids || [];
        if (!covered.length) continue;
        var triggers = n.trigger_product_ids || covered;
        var hasTrigger = triggers.some(function (pid) { return inCartProductIds[pid]; });
        if (!hasTrigger) continue;
        var allCovered = covered.every(function (pid) { return inCartProductIds[pid]; });
        if (allCovered) return n;
      }
      return null;
    }

    // ---- Render the decided state (or hide) ----
    evaluate() {
      var self = this;
      // R-ACCEPTED: a yes anywhere this session ends the pitching. Checked BEFORE
      // the cart read, so a suppressed session costs no fetch, renders nothing and
      // emits nothing on ANY rail (GA4 impression, GA4 null-offer, history record).
      // The in-situ preview short-circuit sits ABOVE this, back in
      // connectedCallback, so a staging review render is never suppressed.
      if (acceptedThisSession()) { this.hide(); return; }
      // Only the NEWEST evaluation may paint: a slower, older read (page load,
      // a cart update and a drawer open can overlap) must not overwrite it.
      var seq = this._evalSeq = (this._evalSeq || 0) + 1;
      getCart().then(function (cart) {
        if (seq !== self._evalSeq) return;
        // D4/SC-4: a covered routine takes precedence over any pitch — render
        // NOTHING and LOG the null offer so the suppression is measurable.
        var covered = self.coveredNullOffer(cart);
        if (covered) { self.hide(); self.logNullOffer(covered); return; }

        var decisions = self.applicableDecisions(cart);
        if (!decisions.length) { self.hide(); return; }
        // D13/SC-19: consume the rotation choice from the history endpoint (or the
        // lead candidate when keyless), render it, and record the impression.
        self.chooseByRotation(decisions).then(function (decision) {
          self.renderCard(decision, cart);
          self.recordImpression(decision);
        });
      }).catch(function () { if (seq === self._evalSeq) self.hide(); });
    }

    hide() {
      this.hidden = true;
      if (this.mount) this.mount.innerHTML = "";
      this._shownOffer = null;
    }

    renderCard(decision, cart) {
      var offer = decision.offer, variant = decision.variant, ov = decision.ov;
      var frag = this.template.content.cloneNode(true);

      // FAIL CLOSED on a cart cast the JS cannot fill (round-8 review). The card is
      // filled by SELECTOR against the cast skin's data-buc-* hooks; only honest-math
      // carries them (upsell_engine.CART_JS_FILL_SUPPORTED — the compiler refuses any
      // other cart cast). If a non-supported cast reached the browser regardless, the
      // required hooks are absent: hide + warn + emit NOTHING rather than throw a
      // TypeError mid-render (which would leave a broken card AND no telemetry).
      var required = ["[data-buc-name]", "[data-buc-accept]", "[data-buc-dismiss]"];
      for (var ri = 0; ri < required.length; ri++) {
        if (!frag.querySelector(required[ri])) {
          if (!this._hookWarnLogged) {
            console.warn("[bundle-upsell] cart card missing " + required[ri] +
                         " (cast skin lacks the JS fill hooks) - hiding, no telemetry.");
            this._hookWarnLogged = true;
          }
          this.hide();
          return;
        }
      }

      // The template renders the cast skin (honest-math by default, wi-2026-0642),
      // so the visible card IS the skin. `card` is the skin root the JS fills.
      var card = frag.querySelector("[data-buc-card]");

      // Hero. The cast skin SERVER-RENDERS a first-paint thumb for cfg.offers[0]
      // into this same slot (wi-2026-0644), so clear it before injecting the
      // DECIDED offer's image - otherwise the two stack up and the shopper sees
      // the wrong product beside the right name. Clearing unconditionally also
      // means an offer with no hero_image collapses the slot (:empty) instead of
      // keeping the placeholder's image.
      var media = frag.querySelector("[data-buc-media]");
      if (media) {
        media.textContent = "";
        if (offer.hero_image) {
          var img = document.createElement("img");
          img.src = offer.hero_image; img.alt = offer.display_name || "";
          img.loading = "lazy"; img.width = 64; img.height = 64;
          media.appendChild(img);
        }
      }

      // Name + subtitle (null-safe: guard every data-buc-* deref so a skin missing
      // an optional hook is simply not filled, never a throw).
      var nameEl = frag.querySelector("[data-buc-name]");
      if (nameEl) nameEl.textContent = offer.display_name || "";
      var sub = frag.querySelector("[data-buc-subtitle]");
      if (offer.need_state && sub) sub.textContent = offer.need_state;
      // CART STEP-UP: name what the bundle holds ("Includes A, B and C"), compiled
      // per bundle by the engine so the card and the CTA use the same names.
      var stepComp = ((this.config.cart_stepup || {}).components || {})[offer.bundle_id];
      if (stepComp && stepComp.line && sub) sub.textContent = stepComp.line;

      // Savings (money token, never a typed number)
      var savings = money(ov.savings_cents || 0);
      var savingsEl = frag.querySelector("[data-buc-savings]");
      if (savingsEl) {
        savingsEl.textContent =
          (ov.savings_cents > 0) ? ("You save " + savings + " vs buying separately") : "";
      }

      // CTA label by variant (offers-wireframe §1 table). Guaranteed present by the
      // required-hooks gate above; still guarded for defense in depth.
      var ctaBtn = frag.querySelector("[data-buc-accept]");
      if (ctaBtn) {
        ctaBtn.textContent = this.ctaLabel(variant, offer, savings, ov);
        ctaBtn.dataset.variant = variant;
      }

      // Free-ship truth guard (W). Composed from the POST-ACCEPT cart, not the
      // pre-accept one (wi-2026-0649 P0 / offer-copy doc F7).
      this.fillFreeShipLine(frag, decision, cart);

      // Fill the honest-math skin's receipt cells for the DECIDED offer. Best-
      // effort + null-safe: a cast skin without a given hook is simply not filled.
      // Presentation only — cart evaluation / kill-check / telemetry are untouched.
      this.fillSkinReceipt(frag, offer, ov);

      // Wire actions (both hooks are guaranteed by the required-hooks gate; guard
      // defensively so a future skin change can never make the wiring throw).
      var self = this;
      if (ctaBtn) ctaBtn.addEventListener("click", function () { self.onAccept(decision, cart); });
      var dismissEl = frag.querySelector("[data-buc-dismiss]");
      if (dismissEl) dismissEl.addEventListener("click", function () {
        self.onDismiss(decision);
      });

      this.mount.innerHTML = "";
      this.mount.appendChild(frag);
      this.hidden = false;

      // Impression (fires once per surface|bundle|variant this pageview)
      this._shownOffer = offer; this._shownVariant = variant; this._shownTrigger = decision.trigger.product_id;
      ga4Event("bundle_upsell_impression", "cart", offer, this.cartSkin(), variant, decision.trigger.product_id);
    }

    ctaLabel(variant, offer, savingsStr, ov) {
      // Library-governed first: the compiled framing strings (folded by the
      // engine, em-dash-lint enforced at compile) are the source of truth for
      // CTA copy. The literals below are a display-only fallback for configs
      // compiled before the framing fold and MUST stay free of U+2014 (SC-4).
      var fr = surfaceFraming(ov, "cart");
      if (variant === "swap_to_save" && fr.cta_swap_to_save) return fr.cta_swap_to_save;
      if (variant === "add" && fr.cta_add) return fr.cta_add;
      if (variant !== "add" && variant !== "swap_to_save" && fr.cta_swap) return fr.cta_swap;
      var name = offer.display_name || "the bundle";
      switch (variant) {
        case "swap_to_save": return "You have both. Swap for " + name + " & save " + savingsStr;
        case "add":          return "Add " + name + " & save " + savingsStr;
        default:             return "Upgrade to " + name + " & save " + savingsStr; // swap
      }
    }

    /* Fill the honest-math skin's receipt for the decided offer (wi-2026-0642).
     * The template renders the cast skin as a placeholder (first offer); this
     * updates the receipt cells to the DECIDED offer's live numbers, computed in
     * JS from the token CENTS in the compiled config (money strings are display-
     * only). Best-effort + null-safe so a skin without a hook (or a non-honest-
     * math cast) is left as its server render. NO cart/kill/telemetry behavior. */
    fillSkinReceipt(frag, offer, ov) {
      function set(sel, text) {
        var el = frag.querySelector(sel);
        if (!el) return;
        el.textContent = text || "";
        if (text) el.hidden = false;
      }
      var tok = (ov && ov.tokens) || {};
      if (tok.standalone_otp_sum_cents != null) set("[data-buc-sep]", money(tok.standalone_otp_sum_cents));
      if (tok.bundle_otp_cents != null) set("[data-buc-total]", money(tok.bundle_otp_cents));
      if (ov && ov.savings_cents != null) set("[data-buc-savings-amt]", money(ov.savings_cents));
      var reasonEl = frag.querySelector("[data-buc-reason]");
      if (reasonEl) {
        if (offer.reason) { reasonEl.textContent = offer.reason; reasonEl.hidden = false; }
        else { reasonEl.hidden = true; }
      }
      var perEl = frag.querySelector("[data-buc-perday]");
      if (perEl) {
        // Library-sourced, compile-folded ONLY (fresh-eyes review 2026-08-07):
        // the engine folds tokens.per_day_amt + tokens.days at compile, and the
        // per-day wording lives in the library (skin_copy labels). Re-deriving
        // the math or hardcoding the words here is a second source of truth
        // that silently diverges when the operator edits the library. If any
        // piece is absent, HIDE the line — the placeholder server render
        // describes a different offer, so stale text is worse than none.
        var labels = ((((this.config || {}).serving || {}).skin_copy ||
                       {})[this.cartSkin()] || {}).labels || {};
        if (tok.per_day_amt && tok.days && labels.per_day_lead && labels.per_day_unit) {
          perEl.textContent = tok.per_day_amt + " " + labels.per_day_lead + " " +
                              tok.days + " " + labels.per_day_unit;
          perEl.hidden = false;
        } else {
          perEl.textContent = "";
          perEl.hidden = true;
        }
      }
    }

    /* ---- Free-ship line, composed from the POST-ACCEPT cart (W truth guard) ----
     * wi-2026-0649 P0. The bug this replaces (offer-copy doc F7): the line was
     * composed from the PRE-accept cart, so on any cart under the threshold it
     * told the shopper to go add money elsewhere while the card's own CTA already
     * crossed the threshold (every compiled bundle clears $45 on its own). The
     * card was arguing against itself on nearly every impression it rendered.
     *
     * The rule is upsell_engine.show_free_shipping(pre, resulting, threshold),
     * evaluated here against the cart the shopper WOULD have the moment they
     * accept:
     *   pre >= threshold              -> nothing (true but not additive)
     *   post >= threshold             -> the COMPILED framing.free_shipping_line
     *   post still < threshold        -> "Add $X", X measured from the POST gap
     *   subscription line in cart     -> nothing (renewals ship free)
     *   post not computable           -> nothing (never a line we cannot stand behind)
     * The below-threshold branch keeps the file's existing wording pattern (no new
     * customer-facing string); it is now simply measured from the right cart. */
    fillFreeShipLine(frag, decision, cart) {
      var fs = frag.querySelector("[data-buc-freeship]");
      if (!fs) return;                                   // skin without the hook
      fs.textContent = "";                               // never inherit a stale line
      fs.hidden = true;
      var thr = this.freeShipThreshold(decision.ov);
      if (!thr) return;                                  // no threshold => no claim
      if (this.cartHasSubscription(cart)) return;        // subs ship free (existing rule)
      var pre = cart.total_price || 0;
      if (pre >= thr) return;                            // already qualifies => not additive
      var post = this.postAcceptSubtotal(cart, decision);
      if (post == null) return;                          // unknown post state => say nothing
      if (post >= thr) {
        // Accepting crosses the threshold: the compiled, library-governed token.
        var line = surfaceFraming(decision.ov, "cart").free_shipping_line || "";
        if (!line) return;                               // pre-fold config => no line
        fs.textContent = line;
        fs.hidden = false;
        return;
      }
      // Still short after accepting: the gap the shopper would ACTUALLY still have.
      fs.textContent = "Add " + money(thr - post) + " for free shipping";
      fs.hidden = false;
    }

    /* Threshold in cents: the card's stamped cfg.free_ship_threshold_cents first
     * (one shop-wide value), else the offer's own compiled token. Live-read is a
     * documented seam (see the header). 0 means "unknown", which suppresses the
     * line rather than guessing. */
    freeShipThreshold(ov) {
      var thr = parseInt(this.dataset.freeShipThreshold, 10);
      if (thr > 0) return thr;
      thr = parseInt(((ov && ov.tokens) || {}).free_shipping_threshold_cents, 10);
      return thr > 0 ? thr : 0;
    }

    /* The cart total (cents) the shopper WOULD have the instant they accept, per
     * CTA variant. Same deltas the accept path itself applies, read off the same
     * two sources of truth (removalVariantIds + the compiled bundle OTP), so the
     * copy can never describe a different mutation than the button performs:
     *   add          -> pre + bundle_otp            (R-ADD zeroes nothing)
     *   swap         -> pre - removed lines + bundle_otp
     *   swap_to_save -> same, with ALL component lines removed
     * Returns null when the bundle price is unknown (a pre-token config): the
     * caller then renders no line at all rather than a number it cannot prove. */
    postAcceptSubtotal(cart, decision) {
      var tok = ((decision && decision.ov) || {}).tokens || {};
      var bundleCents = parseInt(tok.bundle_otp_cents, 10);
      if (!(bundleCents > 0)) return null;
      var pre = cart.total_price || 0;
      if (decision.variant === "add") return pre + bundleCents;
      var removeIds = this.removalVariantIds(decision, decision.variant === "swap_to_save");
      return pre - this.lineTotalFor(cart, removeIds) + bundleCents;
    }

    /* Sum of the FULL line totals for the given variant ids in this cart. Full
     * lines because the swap zeroes the line (updates[vid] = 0), not one unit; a
     * component the cart does not hold contributes 0, which is why a swap that
     * only holds one of several component variants still nets out correctly. */
    lineTotalFor(cart, variantIds) {
      var want = {};
      (variantIds || []).forEach(function (v) { want[v] = true; });
      var sum = 0;
      (cart.items || []).forEach(function (it) {
        if (!want[it.variant_id]) return;
        var line = it.final_line_price;
        if (line == null) line = it.line_price;
        if (line == null) line = (it.final_price != null ? it.final_price : it.price) * it.quantity;
        sum += line || 0;
      });
      return sum;
    }

    // ---- Accept: kill-check → swap/add → validate → events -------------------
    onAccept(decision, preCart) {
      if (this._busy) return;            // click-lock (double-fire guard)
      // R-ACCEPTED: the session already carries a yes, so this card is a stale
      // mount waiting for the next evaluate to clear it. Take it down instead of
      // mutating the cart a second time off one conversation.
      if (acceptedThisSession()) { this.hide(); return; }
      this._busy = true;
      var self = this;
      var btn = this.querySelector("[data-buc-accept]");
      if (btn) btn.setAttribute("aria-busy", "true");

      // ACTION-TIME uncached kill re-check (F14) before any mutation.
      this.actionKillCheck().then(function (killed) {
        if (killed) { self.hide(); self._busy = false; return; }

        var variant = decision.variant;
        var op = (variant === "add") ? self.doAdd(decision, preCart)
                                     : self.doSwap(decision, preCart, variant === "swap_to_save");
        op.then(function (result) {
          if (result.ok) {
            // R-ACCEPTED: written ONLY here, i.e. after the kill-check passed AND
            // conservation validated the returned cart. A rejected or rolled-back
            // accept must never silence the rest of the session.
            setAcceptedThisSession(decision.offer.bundle_id, variant, "cart");
            ga4Event("bundle_upsell_accept", "cart", decision.offer, self.cartSkin(), variant, decision.trigger.product_id);
            // D13 durable ledger: the outcome, not just the show (wi-2026-0649 P1).
            var recorded = self.recordOutcome(decision, "accept");
            self._shownOffer = null; // accepted ⇒ no decline for this journey
            // Refresh the theme's cart UI the native way.
            themePublish(CART_UPDATE_EVENT, { source: "bundle-upsell", cart: result.cart });
            // wi-2026-1204: the drawer/cart-page subscriptions are gated on window.PUB_SUB_EVENTS,
            // which a top-level const never sets, so the publish above is unheard. Repaint directly,
            // from the cart this accept's own write returned: no re-fetch, no timer, no page reload
            // (wi-2026-1216). The ledger write's promise rides along for any caller that needs it.
            Array.prototype.forEach.call(document.querySelectorAll("cart-drawer, cart-page-items"), function (el) {
              if (typeof el.reload === "function") el.reload(result.cart, recorded);
            });
          } else {
            self.showError(result.message || "Something went wrong. Refreshing your cart…", result.reload);
          }
        }).catch(function () {
          self.showError("Something went wrong. Refreshing your cart…", true);
        }).finally(function () {
          self._busy = false;
          if (btn) btn.removeAttribute("aria-busy");
        });
      });
    }

    /* The component variant id(s) an accept REMOVES from the cart. ONE definition,
     * used by BOTH swap paths and by the post-accept free-ship math (wi-2026-0649),
     * so the copy and the mutation can never drift apart: swap_to_save clears every
     * component variant, a plain swap clears the offer variant's declared component
     * set (falling back to the trigger line when the config carries none), and an
     * `add` never calls this at all. */
    removalVariantIds(decision, isSwapToSave) {
      var ov = decision.ov || {};
      if (isSwapToSave) return this.allComponentVariantIds(decision.offer);
      if (ov.component_variant_ids && ov.component_variant_ids.length) {
        return ov.component_variant_ids.slice();
      }
      return [decision.trigger.variant_id];
    }

    /* PRIMARY swap: SINGLE-REQUEST /cart/update.js. Zeroes the component variant(s)
     * this bundle replaces AND adds the bundle variant AND self-writes attributes —
     * one atomic request (D7 F16). swap_to_save zeroes ALL component variants. */
    doSwap(decision, preCart, isSwapToSave) {
      var self = this;
      var offer = decision.offer, ov = decision.ov;
      if (this.config.swap_mode === "sequential") {
        return this.doSwapSequential(decision, preCart, isSwapToSave); // FALLBACK
      }

      // Fingerprint the component variant(s) to remove.
      var removeVariantIds = this.removalVariantIds(decision, isSwapToSave);

      var updates = {};
      removeVariantIds.forEach(function (vid) { updates[vid] = 0; });     // remove
      // Add the bundle variant (update.js adds a new line when the keyed variant
      // isn't already in the cart — proven).
      updates[ov.bundle_variant_id] = (updates[ov.bundle_variant_id] || 0) + 1;

      var body = JSON.stringify({
        updates: updates,
        attributes: this.attributePayload(offer), // D5 cart-attribute self-write
      });

      return fetch(cartRoot().update + ".js", Object.assign({}, jsonFetchConfig(), { body: body }))
        .then(function (r) { return r.json().then(function (cart) { return { status: r.status, cart: cart }; }); })
        .then(function (res) {
          if (res.status !== 200) {
            // Atomic reject — nothing partial changed. Loud, recoverable, no comp.
            return { ok: false, message: "Couldn’t complete the swap. Your cart is unchanged.", reload: false };
          }
          var check = self.validateConservation(preCart, res.cart, offer, ov, removeVariantIds, isSwapToSave);
          if (!check.ok) {
            // Mismatch ⇒ reload + error, NO compensating blind mutation (F16).
            return { ok: false, message: "Your cart needs a refresh to finish that.", reload: true };
          }
          // Elevar signal quality: one remove per component + one add for the bundle.
          removeVariantIds.forEach(function (vid) {
            var comp = self.componentElevarByVariant(offer, vid);
            elevarCart("remove", comp || { variant_id: vid }, 1);
          });
          elevarCart("add", ov.elevar, 1);
          return { ok: true, cart: res.cart };
        });
    }

    /* ADD variant (qty 2+): add the bundle, NEVER zero the multi-qty line. */
    doAdd(decision, preCart) {
      var self = this;
      var offer = decision.offer, ov = decision.ov;
      // Single request: add + attribute write via /cart/update.js.
      var updates = {};
      updates[ov.bundle_variant_id] = 1;
      var body = JSON.stringify({ updates: updates, attributes: this.attributePayload(offer) });
      return fetch(cartRoot().update + ".js", Object.assign({}, jsonFetchConfig(), { body: body }))
        .then(function (r) { return r.json().then(function (cart) { return { status: r.status, cart: cart }; }); })
        .then(function (res) {
          if (res.status !== 200) return { ok: false, message: "Couldn’t add the bundle. Your cart is unchanged.", reload: false };
          // Conservation: bundle present at +1, component line untouched.
          var bundleQty = self.variantQtyIn(res.cart, ov.bundle_variant_id);
          var preBundleQty = self.variantQtyIn(preCart, ov.bundle_variant_id);
          if (bundleQty !== preBundleQty + 1) return { ok: false, message: "Your cart needs a refresh to finish that.", reload: true };
          elevarCart("add", ov.elevar, 1);
          return { ok: true, cart: res.cart };
        });
    }

    /* FALLBACK sequential swap (only if config.swap_mode === 'sequential'):
     * ADD-BUNDLE-FIRST → verify → zero component. Fails toward a VISIBLE duplicate. */
    doSwapSequential(decision, preCart, isSwapToSave) {
      var self = this;
      var offer = decision.offer, ov = decision.ov;
      var removeVariantIds = this.removalVariantIds(decision, isSwapToSave);

      // Step 1: add the bundle (+ attributes) FIRST.
      var addBody = JSON.stringify({ id: ov.bundle_variant_id, quantity: 1 });
      return fetch(cartRoot().add + ".js", Object.assign({}, jsonFetchConfig(), { body: addBody }))
        .then(function (r) { if (r.status !== 200) throw new Error("add failed"); return r.json(); })
        // Write attributes (add.js can't set them) via a no-qty update.
        .then(function () {
          return fetch(cartRoot().update + ".js", Object.assign({}, jsonFetchConfig(), {
            body: JSON.stringify({ attributes: self.attributePayload(offer) }),
          }));
        })
        // Step 2: zero the component(s).
        .then(function () {
          var updates = {};
          removeVariantIds.forEach(function (vid) { updates[vid] = 0; });
          return fetch(cartRoot().update + ".js", Object.assign({}, jsonFetchConfig(), {
            body: JSON.stringify({ updates: updates }),
          }));
        })
        .then(function (r) { return r.json().then(function (cart) { return { status: r.status, cart: cart }; }); })
        .then(function (res) {
          // If the remove failed, we have a VISIBLE duplicate (bundle + component) —
          // loud + recoverable, surfaced by the conservation check as a reload.
          var check = self.validateConservation(preCart, res.cart, offer, ov, removeVariantIds, isSwapToSave);
          if (!check.ok) return { ok: false, message: "Your cart has both items. Please review before checkout.", reload: true };
          removeVariantIds.forEach(function (vid) {
            var comp = self.componentElevarByVariant(offer, vid);
            elevarCart("remove", comp || { variant_id: vid }, 1);
          });
          elevarCart("add", ov.elevar, 1);
          return { ok: true, cart: res.cart };
        });
    }

    /* Conservation check (PT-3): bundle variant present at +1 AND every removed
     * component variant is gone. Structural (deterministic) — the money delta is
     * NOT asserted equal because swap-to-save deliberately LOWERS the total by the
     * bundle savings; we log it advisorily. */
    validateConservation(preCart, postCart, offer, ov, removeVariantIds, isSwapToSave) {
      var preBundle = this.variantQtyIn(preCart, ov.bundle_variant_id);
      var postBundle = this.variantQtyIn(postCart, ov.bundle_variant_id);
      if (postBundle !== preBundle + 1) return { ok: false, reason: "bundle_not_added" };
      for (var i = 0; i < removeVariantIds.length; i++) {
        if (this.variantQtyIn(postCart, removeVariantIds[i]) !== 0) {
          return { ok: false, reason: "component_not_removed" };
        }
      }
      // Advisory: bundle price should not EXCEED the removed components' sum.
      // (Deliberate give-back means post_total <= pre_total; never assert equality.)
      return { ok: true };
    }

    // ---- Dismiss (R-DISMISS): set session memory, hide, emit dismiss ----------
    onDismiss(decision) {
      // R-ACCEPTED (review follow-up, wi-2026-0649): a stale-mounted card's
      // dismiss must stay silent once the session is suppressed, matching
      // evaluate()/onPageHide/onAccept -- telemetry-only edge, no mutation.
      if (acceptedThisSession()) return;
      setDismissed(decision.offer.bundle_id);
      ga4Event("bundle_upsell_dismiss", "cart", decision.offer, this.cartSkin(), decision.variant, decision.trigger.product_id);
      // D13 durable ledger: a dismissal was session-only until now, so the ledger
      // knew what we showed and nothing about what happened (wi-2026-0649 P1).
      this.recordOutcome(decision, "dismiss");
      this._shownOffer = null; // dismissed ⇒ not a decline
      this.hide();
    }

    // ---- Decline: shown but not accepted/dismissed at journey end ------------
    onPageHide() {
      // R-ACCEPTED: nothing is emitted on any rail once the session has a yes.
      if (acceptedThisSession()) { this._shownOffer = null; return; }
      if (this._shownOffer) {
        ga4Event("bundle_upsell_decline", "cart", this._shownOffer, this.cartSkin(), this._shownVariant, this._shownTrigger);
        this._shownOffer = null;
      }
    }

    showError(msg, reload) {
      var err = this.querySelector("[data-buc-error]");
      if (err) { err.textContent = msg; err.hidden = false; }
      if (this.live) this.live.textContent = msg;
      if (reload) { setTimeout(function () { window.location.reload(); }, 1400); }
    }

    /* ---- ACTION-TIME uncached kill check (F14 / SC-8 — FAIL CLOSED) ----
     * Implements the frozen contract upsell_engine.action_time_killcheck_contract():
     * GET config.action_kill_url no-store, with a cache-buster + the surface, under
     * a HARD 1500ms timeout. Body: { surface, enabled(bool), revision, checked_at };
     * `enabled` is the ONLY field trusted. Proceed ONLY on a 200 with
     * enabled === true. A non-200, a timeout, a network error, a malformed body, or
     * a MISSING URL ALL read as KILLED (resolve(true)) — never a silent proceed.
     * Returns Promise<boolean> where true === killed. */
    actionKillCheck() {
      var url = this.dataset.actionKillUrl;
      if (!url) {
        // The R4.1 app-proxy seam is landed (the engine emits action_kill_url on
        // every compile). A rendered card with no URL is a misconfiguration, and a
        // kill switch that cannot be confirmed must FAIL CLOSED — abort the action.
        if (!this._killNoteLogged) {
          console.warn("[bundle-upsell] no action_kill_url wired - failing CLOSED (aborting the action).");
          this._killNoteLogged = true;
        }
        return Promise.resolve(true); // killed
      }
      var bust = url + (url.indexOf("?") === -1 ? "?" : "&") + "surface=cart&t=" + Date.now();
      var controller = (typeof AbortController !== "undefined") ? new AbortController() : null;
      var timer = setTimeout(function () { if (controller) controller.abort(); }, 1500);
      var opts = { cache: "no-store", headers: { Accept: "application/json" } };
      if (controller) opts.signal = controller.signal;
      return fetch(bust, opts)
        .then(function (r) {
          if (!r.ok) return true;                        // non-200 ⇒ killed
          return r.json()
            .then(function (data) { return !(data && data.enabled === true); }) // only enabled===true proceeds
            .catch(function () { return true; });        // malformed body ⇒ killed
        })
        .catch(function () { return true; })             // timeout / network error ⇒ killed
        .then(function (killed) { clearTimeout(timer); return killed; });
    }

    /* ---- Offer-history seam (D13/D4) — best-effort, key-gated ----
     * The runtime records impressions + null offers + consumes the rotation choice
     * from the offer-history app-proxy endpoints. Every call is best-effort: a
     * recording failure NEVER breaks the card. Per-customer state needs a key
     * (identified customer id or the durable anon session cookie a later theme unit
     * owns); a keyless session degrades to the status quo, GA4 still measuring. */
    historyApiUrl() {
      if (this.dataset.historyApiUrl) return this.dataset.historyApiUrl;
      if (this.config && this.config.history_api_url) return this.config.history_api_url;
      // Derive from the killcheck APP-PROXY sibling: /apps/bundle-engine/killcheck
      // -> /apps/bundle-engine/history. Same app proxy, same deploy - no new field.
      //
      // Gated on the url actually BEING the app proxy (2026-08-22). The killcheck
      // endpoint has a documented fallback shape: when the Shopify app proxy is
      // not configured yet, the compiled action_kill_url is the killcheck
      // service's own absolute host instead. That host serves killcheck ONLY, so
      // deriving a /history sibling off it would point every history call at a
      // 404. The calls are best-effort (a failure never breaks the card), but a
      // silent misdirect is worse than an honest "no endpoint": with no url the
      // runtime cleanly degrades to the lead candidate and GA4 keeps measuring.
      var kill = this.dataset.actionKillUrl || "";
      if (kill.indexOf("/apps/") !== -1 && kill.indexOf("/killcheck") !== -1) {
        return kill.replace("/killcheck", "/history");
      }
      return "";
    }
    resolveKeys() {
      return { customer_id: this.dataset.customerId || "", session_key: this.readSessionKey() };
    }
    readSessionKey() {
      // The durable first-party anonymous session key (offer_history Assumption 11)
      // is a functional cookie owned by a later, touch-set-disjoint theme unit.
      // Read it if present; absent ⇒ anonymous sessions stay stateless.
      try {
        var m = document.cookie.match(/(?:^|;\s*)om_offer_session_key=([^;]+)/);
        return m ? decodeURIComponent(m[1]) : "";
      } catch (e) { return ""; }
    }
    postHistory(op, body) {
      var base = this.historyApiUrl();
      if (!base) return Promise.resolve(null);
      var url = base.replace(/\/+$/, "") + "/" + op;
      var opts = Object.assign({}, jsonFetchConfig(), { body: JSON.stringify(body), cache: "no-store" });
      return fetch(url, opts)
        .then(function (r) { return r.ok ? r.json().catch(function () { return null; }) : null; })
        .catch(function () { return null; }); // best-effort: never break the card
    }

    /* D13/SC-19: pick which offer to show from the applicable set, rotating past
     * what this customer has already seen. Keyless / single-candidate / no endpoint
     * ⇒ the lead candidate (status quo). Endpoint error ⇒ the lead candidate. */
    chooseByRotation(decisions) {
      if (decisions.length === 1) return Promise.resolve(decisions[0]);
      var keys = this.resolveKeys();
      if (!this.historyApiUrl() || (!keys.customer_id && !keys.session_key)) {
        return Promise.resolve(decisions[0]);
      }
      var candidateIds = decisions.map(function (d) { return d.offer.bundle_id; });
      return this.postHistory("rotate", {
        candidate_offer_ids: candidateIds,
        customer_id: keys.customer_id || null,
        session_key: keys.session_key || null
      }).then(function (res) {
        var chosenId = res && res.offer_id;
        if (chosenId) {
          for (var i = 0; i < decisions.length; i++) {
            if (decisions[i].offer.bundle_id === chosenId) return decisions[i];
          }
        }
        return decisions[0];
      }).catch(function () { return decisions[0]; });
    }

    /* D13: record an offer OUTCOME with its serving envelope (surface + variant).
     * ONE shape, ONE op: the endpoint's `record` op is offer_history.record_
     * impression(), whose `outcome` argument already carries the four-value
     * vocabulary (impression | accept | decline | dismiss) and whose column already
     * exists. So an accept or a dismiss row is the SAME post with the outcome
     * named, not a new transport (wi-2026-0649 P1, closing the doc's "the ledger
     * knows what we showed and nothing about what happened" gap).
     * Deduped per (outcome|bundle|variant) per pageview so a cart re-render cannot
     * double-count. Keyless ⇒ nothing recorded (the GA4 rail still measures).
     * Best-effort throughout: postHistory swallows every failure, so a ledger
     * write can never break the cart. */
    recordOutcome(decision, outcome) {
      var keys = this.resolveKeys();
      if (!keys.customer_id && !keys.session_key) return Promise.resolve(null);
      var k = outcome + "|" + decision.offer.bundle_id + "|" + decision.variant;
      if (this._recorded[k]) return Promise.resolve(null);
      this._recorded[k] = true;
      return this.postHistory("record", {
        offer_id: decision.offer.bundle_id,
        surface: "cart",
        outcome: outcome,
        variant_assignment: decision.variant,
        customer_id: keys.customer_id || null,
        session_key: keys.session_key || null
      });
    }
    // A rendered offer's impression is just the impression-outcome row.
    recordImpression(decision) { this.recordOutcome(decision, "impression"); }

    /* D4/SC-4: log a null offer (routine already covered). Two rails, once per
     * covered routine per pageview: the GA4 aggregate event (-> upsell_events ETL,
     * wi-2026-0629) and the best-effort per-customer offer-history POST. */
    logNullOffer(nullOffer) {
      var routine = nullOffer.bundle_id || nullOffer.trigger_sku || "unknown";
      var dedupeKey = "null|cart|" + routine;
      if (_emitted[dedupeKey]) return;
      _emitted[dedupeKey] = true;
      emitGA4({
        event: "bundle_upsell_null_offer",
        upsell_surface: "cart",
        upsell_offer_type: "null_offer",
        upsell_trigger_product: String(routine),
        // A covered routine is still SERVED by a skin, so the cast skin is the
        // serve fact here too — carrying it keeps null-offer suppression
        // measurable per (skin x device). offer_type='null_offer' is the null
        // marker; there is no CTA on a null offer.
        upsell_variant: this.cartSkin(),
        upsell_cta_variant: "null_offer",
        bundle_id: nullOffer.bundle_id || ""
      });
      this.postHistory("null", { surface: "cart", covered_routine: String(routine) });
    }

    // ---- Cart-attribute payload (D5): bundle_id + bundle_src self-write -------
    attributePayload(offer) {
      return { bundle_id: offer.bundle_id, bundle_src: "upsell_cart" };
    }

    /* The CAST SKIN for this cart serve — the serve fact GA4's upsell_variant must
     * carry (mirrors the PDP block, which threads cfg.serving.casting.pdp -> skin
     * into its impression). The card Liquid stamps the cast on the host element as
     * data-cart-skin (from cfg.serving.casting.cart); we read that first, fall back
     * to the config's casting map, then to the honest-math default the card Liquid
     * uses — so the emitted upsell_variant is always a real skin, never blank and
     * never a swap/add CTA label. Which skin the cart wears is CONFIG, not code. */
    cartSkin() {
      if (this.dataset.cartSkin) return this.dataset.cartSkin;
      var casting = this.config && this.config.serving && this.config.serving.casting;
      var cart = casting && casting.cart;
      if (cart && cart.default) return cart.default;
      return "honest-math";
    }

    // ---- Small config/cart utilities ----
    resolveVariant(offer, triggerItem) {
      if (offer.variant_map && triggerItem && offer.variant_map[triggerItem.variant_id]) {
        return offer.variant_map[triggerItem.variant_id];
      }
      return offer.offer_variant;
    }
    allBundleVariantIds() {
      var ids = [];
      this.config.offers.forEach(function (o) {
        if (o.offer_variant && o.offer_variant.bundle_variant_id) ids.push(o.offer_variant.bundle_variant_id);
        if (o.variant_map) Object.keys(o.variant_map).forEach(function (k) {
          if (o.variant_map[k].bundle_variant_id) ids.push(o.variant_map[k].bundle_variant_id);
        });
      });
      return ids;
    }
    allComponentVariantIds(offer) {
      // Union of component variant ids across the offer variant + variant_map.
      var set = {};
      function add(ov) { (ov && ov.component_variant_ids || []).forEach(function (v) { set[v] = true; }); }
      add(offer.offer_variant);
      if (offer.variant_map) Object.keys(offer.variant_map).forEach(function (k) { add(offer.variant_map[k]); });
      return Object.keys(set).map(Number);
    }
    componentElevarByVariant(offer, variantId) {
      // Optional per-component Elevar payloads (config seam); null ⇒ minimal remove.
      if (!offer.components) return null;
      for (var i = 0; i < offer.components.length; i++) {
        if (offer.components[i].variant_id === variantId) return offer.components[i].elevar || offer.components[i];
      }
      return null;
    }
    cartHasAllComponents(cart, offer) {
      var need = offer.all_component_product_ids || offer.trigger_product_ids || [];
      if (need.length < 2) return false; // "all components" only meaningful for multi-component sets
      var have = {};
      cart.items.forEach(function (it) { have[it.product_id] = true; });
      return need.every(function (pid) { return have[pid]; });
    }
    cartHasGift(cart, offer) {
      var gifts = (this.config.gift_product_ids || []);
      if (!gifts.length) return false;
      return cart.items.some(function (it) { return gifts.indexOf(it.product_id) !== -1; });
    }
    ownsAllTriggers(offer) {
      // R-OWN graceful degrade: suppress only if config supplies owned state.
      var owned = this.config.owned_product_ids;
      if (!owned || !owned.length) return false;
      return (offer.trigger_product_ids || []).every(function (pid) { return owned.indexOf(pid) !== -1; });
    }
    cartHasSubscription(cart) {
      return cart.items.some(function (it) { return it.selling_plan_allocation != null; });
    }
    variantQtyIn(cart, variantId) {
      var q = 0;
      (cart.items || []).forEach(function (it) { if (it.variant_id === variantId) q += it.quantity; });
      return q;
    }
  }

  // Expose the GA4 emitter for the PDP block's inline impression (shared dedupe).
  window.OMBundleUpsell = window.OMBundleUpsell || {};
  window.OMBundleUpsell.emitGA4 = emitGA4;
  // R-ACCEPTED seam: the other surfaces (PDP block, homepage strip) live in their
  // own files, but the suppression is meant to be total across surfaces. Export the
  // reader so those gates consume ONE implementation of the session flag instead of
  // re-deriving the key. Purely additive: nothing here changes if nobody reads it.
  window.OMBundleUpsell.acceptedThisSession = acceptedThisSession;

  if (!customElements.get("bundle-upsell-card")) {
    customElements.define("bundle-upsell-card", BundleUpsellCard);
  }
})();
